import React from 'react';
import { Link } from 'react-router-dom';
import styles from '../styles/Button.module.css';
import FaIcon from './FaIcon';
import { buildGoogleInterstitialEligibilityAttributes } from '../ads/googleInterstitialAttributes';

/**
 * Inline loading dots animation
 */
const LoadingDots = () => (
  <span className={styles.loadingDots}>
    <span className={styles.loadingDot} />
    <span className={styles.loadingDot} />
    <span className={styles.loadingDot} />
  </span>
);

/**
 * Inline loading spinner
 */
const LoadingSpinner = ({ dark = false }) => (
  <span className={dark ? styles.loadingSpinnerDark : styles.loadingSpinner} />
);

const Button = ({
  to,
  primary = false,
  secondary = false,
  fullWidth = false,
  disabled = false,
  loading = false,
  loadingText,
  loadingStyle = 'dots', // 'dots' | 'spinner' | 'text'
  onClick,
  allowGoogleInterstitial = false,
  children,
  className = '',
  type = 'button',
  ...rest
}) => {
  let buttonClass = styles.button;
  if (primary) buttonClass += ' ' + styles.primary;
  if (secondary) buttonClass += ' ' + styles.secondary;
  if (fullWidth) buttonClass += ' ' + styles.fullWidth;
  if (disabled || loading) buttonClass += ' ' + styles.disabled;
  if (loading) buttonClass += ' ' + styles.loading;
  if (className) buttonClass += ' ' + className;

  // Whitelist only safe DOM props to avoid leaking unknown props (e.g., afterSignUpUrl) to DOM
  const pickDomProps = (props) => {
    const allowed = ['title', 'aria-label', 'role', 'target', 'rel', 'tabIndex', 'aria-disabled', 'id', 'name', 'value', 'style'];
    const out = {};
    for (const key of allowed) if (key in props) out[key] = props[key];
    return out;
  };
  const domProps = pickDomProps(rest);
  const googleInterstitialAttrs = buildGoogleInterstitialEligibilityAttributes({
    allowGoogleInterstitial,
  });

  // Render button content based on loading state
  const renderContent = () => {
    if (!loading) return children;

    // Use custom loading text if provided
    const text = loadingText !== undefined ? loadingText : children;

    switch (loadingStyle) {
      case 'spinner':
        return (
          <span className={styles.loadingContent}>
            {text}
            <LoadingSpinner dark={!primary} />
          </span>
        );
      case 'text':
        return text;
      case 'dots':
      default:
        return (
          <span className={styles.loadingContent}>
            {text}
            <LoadingDots />
          </span>
        );
    }
  };

  if (to) {
    return (
      <Link
        to={to}
        className={buttonClass}
        aria-disabled={disabled || loading}
        aria-busy={loading}
        tabIndex={disabled || loading ? -1 : 0}
        {...googleInterstitialAttrs}
        onClick={(disabled || loading) ? (e) => e.preventDefault() : onClick}
        {...domProps}
      >
        {renderContent()}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={buttonClass}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading}
      {...domProps}
    >
      {renderContent()}
    </button>
  );
};

export default Button;

export const IconButton = ({
  to,
  onClick,
  disabled = false,
  loading = false,
  allowGoogleInterstitial = false,
  iconClassName = '',
  className = '',
  type = 'button',
  ...rest
}) => {
  let buttonClass = styles.iconButton;
  if (disabled || loading) buttonClass += ' ' + styles.disabled;
  if (loading) buttonClass += ' ' + styles.iconLoading;
  if (className) buttonClass += ' ' + className;

  if (to) {
    const googleInterstitialAttrs = buildGoogleInterstitialEligibilityAttributes({
      allowGoogleInterstitial,
    });

    return (
      <Link
        to={to}
        className={buttonClass}
        aria-disabled={disabled || loading}
        aria-busy={loading}
        {...googleInterstitialAttrs}
        tabIndex={(disabled || loading) ? -1 : 0}
        {...rest}
        onClick={(disabled || loading) ? (e) => e.preventDefault() : undefined}
      >
        {loading ? <LoadingSpinner /> : <FaIcon className={iconClassName} />}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={buttonClass}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading}
      {...rest}
    >
      {loading ? <LoadingSpinner /> : <FaIcon className={iconClassName} />}
    </button>
  );
};
